import * as constants from '@/common/constants';
import {mapGetters} from 'vuex';
import OrdersQueue from '@/common/modules/orders-queue';
import {Severity} from '@sentry/browser';
import PgApi from '@/common/api/pg-api';
import * as mutations from '@/app/store/mutations';
import OrionApi from '@/common/api/orion-api';
import cancelService from '@/app/services/cancel';
import MixinStorage from '@/common/mixins/storage';
import logger from '@pubfac/log-bundle';
import getCountryISO3 from 'country-iso-2-to-3';

export default {
  data: () => ({
    ordersQueue: null,
    isOrdering: false
  }),
  mixins: [
    MixinStorage
  ],
  computed: {
    ...mapGetters([
      'currentChoiceConfig',
      'orderComplete',
      'choices',
      'currentOrderUid',
      'paymentMethod',
      'alreadyClickedChecked',
      'isAspspEligibleSepa',
      'isAspspEligibleBankCard',
    ]),
    isInitialOrderForm() {
      return this.choices && this.choices.length && this.choices[0].type === constants.ORDER_STEP_INITIAL;
    }
  },
  methods: {
    /**
     * Process the oldest order inserted in queue on "orderInQueue" event in a synchronized way.
     * When the oldest order is successfully processed, remove it then process the next order.
     * If a pending order fails, stop processing. Failed orders will stack until API calls works again.
     * If a completed order fails, retry 10 times then redirect to error page.
     */
    initOrdersQueue() {
      this.ordersQueue = new OrdersQueue();
      this.$on('orderInQueue', () => {
        // Block concurrent ordering
        if (this.isOrdering) {
          return;
        }

        this.isOrdering = true;
        // Retrieve the oldest order in queue
        const order = this.ordersQueue.next();
        // Check order. If there is no data, log error then process next order.
        if (!order.data) {
          this.sendSentryLog(
            Severity.Fatal,
            'Missing order\'s data, go to next order.',
            {order},
            this.sfId ? null : {email: this.getCurrentEmailAddress()});
          this.isOrdering = false;
          this.ordersQueue.remove();
          if (this.ordersQueue.size()) {
            this.$emit('orderInQueue');
          }

          return;
        }

        // Increment the number of attempts to send current order.
        // If there is more than 3 attempts for non-completed orders
        // or more thant 10 attempts for completed orders
        order.count++;
        if ((order.data.status !== constants.ORDER_STATUS_COMPLETED && order.count > 3) ||
          (order.data.status === constants.ORDER_STATUS_COMPLETED && order.count > 10)) {
          // - if order is not completed, drop the order and process next
          if (order.data.status !== constants.ORDER_STATUS_COMPLETED) {
            this.isOrdering = false;
            this.ordersQueue.remove();
            if (this.ordersQueue.size()) {
              this.$emit('orderInQueue');
            }

            return;
          }

          // - else, the order is completed, send an error to Sentry then redirect to error page.
          this.sendSentryLog(
            Severity.Fatal,
            'Unable to send completed order to Orion.',
            {order},
            this.sfId ? null : {email: this.getCurrentEmailAddress()});
          this.redirectToErrorPage();
          return;
        }

        // Try to send the order to Orion
        this.$log.debug('Send next order data', order);
        this.sendRegularOrderData(constants.ORDER_STEP_INITIAL, order.data).then(() => {
          this.isOrdering = false;
          this.ordersQueue.remove();
          if (this.ordersQueue.size()) {
            this.$emit('orderInQueue');
          }
        }).catch(error => {
          this.$log.error('Unable to send order data', error);
          this.isOrdering = false;

          // First retry after 100ms
          // Second retry after 400ms
          // ...
          // Tenth retry after 10sec
          setTimeout(() => {
            this.$emit('orderInQueue');
          }, 100 * (order.count ** 2));
        });
      });
    },

    /**
     * For initial orders on regular order forms, we send multiple POST /orders to Orion
     * as there is the order draft mechanism. To avoid concurrent calls and preserve their ordering,
     * we use a synchronized queue to perform calls one by one.
     */
    async enqueueRegularOrder() {
      await this.prepareRegularOrderData(constants.ORDER_STEP_INITIAL).then(data => {
        const order = {
          id: this.Utils.generateUID(),
          timestamp: (new Date()).getTime(),
          data,
          count: 0 // Store the number of attempts to send the order to Orion
        };

        this.$log.debug('Enqueue new order', order);
        this.ordersQueue.add(order);
        this.$emit('orderInQueue');
      }).catch(error => {
        this.sendSentryLog(Severity.Error, error.message, {}, null, false);
      });
    },

    /**
     * Retrieve contact data (email, address, etc.).
     * sfId must have been stored previously.
     */
    getContactData() {
      return new Promise((resolve, reject) => {
        if (!this.sfId) {
          this.$log.debug('Missing sfId to retrieve prefill data');
          reject();
          return;
        }

        // Call pgSf API
        const pgApi = new PgApi();

        pgApi.getPrefillData(this.sfId, this.affiliateConfig.affiliateId, false).then(data => {
          this.$log.debug('Prefill data', data.content);
          this.$store.commit(mutations.SET_PREFILL_DATA, data.content);
          resolve(data.content);
        }).catch(error => {
          this.sendSentryLog(
            Severity.Error,
            'Unable to retrieve prefill data',
            {error});
          reject();
        });
      });
    },


    /**
     * Action: post (initial or cross-sell), put (initial, when updating order draft and completed order)
     *
     * @param orderType {string} 'initial' | 'crosssell'
     * @param data {object} JSON payload for Orion
     * @returm {Promise}
     */
    sendRegularOrderData(orderType, data) {
      return new Promise((resolve, reject) => {
        const action = this.getOrderDataAction(orderType);

        if (data.billingCountry && data.billingCountry.length === 2) {
          data.billingCountry = getCountryISO3(data.billingCountry.toUpperCase()) || data.billingCountry;
        }

        if (data.shippingCountry && data.shippingCountry.length === 2) {
          data.shippingCountry = getCountryISO3(data.shippingCountry.toUpperCase()) || data.shippingCountry;
        }

        const orderData = {
          action,
          data
        };

        logger.log(1, '[vanguard] formValid, sendOrderData', orderData);
        // If shipping information is provided, insert it into orderData
        if (this.formData.shipping.shippingFirstName) {
          Object.assign(orderData.data, this.formData.shipping);
        }

        // For initial orders, store orderDraftSent=true before sending first POST
        let isInitialPost = false;
        if (orderType === constants.ORDER_STEP_INITIAL && !this.orderDraftSent) {
          this.$store.commit(mutations.SET_ORDER_DRAFT_SENT, true);
          isInitialPost = true;
        }

        // If testLevel is vanguard, do not call Orion and finish process
        if (this.testLevel === constants.TEST_LEVEL_VANGUARD) {
          // If order is not complete, do nothing and exit
          if (!this.orderComplete) {
            resolve();
            return;
          }

          // Else, redirect to confirm page
          // Check is it's a dynamicChoice, if yes just redirect to the confirmation page of it (on the same step)
          this.orderConfirmation();
          return resolve();
        }

        // If the user has accepted Palyance PREFILL and is ASPSP
        // Meaning that the paymentInformationIdentifier is provided by the eligibility call, not the returns of the iframe
        if (this.orderComplete && ((this.isAspspEligibleSepa && this.paymentMethod === constants.PAYMENT_METHOD_SEPA) || (this.isAspspEligibleBankCard && this.paymentMethod === constants.PAYMENT_METHOD_BANK_CARD))) {
          this.orderConfirmation();
          return;
        }

        let capturedNow = false;
        if (action === 'createOneClick' && orderData.data.paymentType === 'bank_card') {
          this.$cookies.set('CAPTURED_NOW', true);
          capturedNow = true;
          orderData.data.alreadyClickedChecked = this.alreadyClickedChecked;
        }

        if (this.$cookies.get('CAPTURED_NOW')) {
          capturedNow = true;
        }

        console.log('capturedNow', capturedNow);

        const orionApi = new OrionApi(this.$store, capturedNow);
        orionApi.postOrder(orderData).then(response => {
          this.$log.debug('Order call to Orion successful', response);
          // If order is not complete, exit
          if (!this.orderComplete) {
            return resolve();
          }

          if (this.journeyConfig.orderFormType === constants.ORDER_FORM_TYPE_1CLICK) {
            this.getContactData()
              .then(() => {
                // Store contact prefill data
                this.storePrefillData();
              })
              .catch(() => {
                this.$log.error('Unable to retrieve contact data to get country to compute shipping price');
              })
              .finally(() => {
                if (capturedNow) {
                  this.processOrderCapturedNow();
                } else {
                  this.orderConfirmation();
                }
              });
          } else {
            // Else, redirect to confirm page
            if (capturedNow) {
              this.processOrderCapturedNow();
            }
            else {
              console.log('calling orderConfirmation');
              this.orderConfirmation();
            }
          }

          resolve();
        }).catch(error => {
          // For initial orders, rollback orderDraftSent=false if first POST failed
          if (orderType === constants.ORDER_STEP_INITIAL && isInitialPost) {
            this.$store.commit(mutations.SET_ORDER_DRAFT_SENT, false);
          }

          this.sendSentryLog(
            Severity.Fatal,
            'Order call to Orion failed',
            {error});
          reject(error);
        });
      });
    },

    /**
     * Action: patch (cancel)
     */
    sendUpdatePreviousOrder() {
      // If testLevel is vanguard, do not call Orion
      if (this.testLevel === constants.TEST_LEVEL_VANGUARD) {
        this.$store.commit(mutations.SET_IS_PREVIOUS_ORDER_CANCELLED, true);
        return;
      }

      // Get the cancelConfig
      const cancelConfig = cancelService.getCancelConfig(this.journeyConfig, this.localConfig);

      // Prepare order data for Orion
      const orderData = {
        action: 'cancelled',
        data: {
          testLevel: this.testLevel ? this.testLevel : null,
          externalOrderId: this.previousExternalOrderId,
          orderLines: [],
          status: 'orderFormCancelled'
        }
      };

      // If there is a mail to send, we set it
      if (cancelConfig.emailContentId) {
        orderData.data.cancelEmailContentId = cancelConfig.emailContentId;
      }

      //  Delete key from redis only if we are not in an upsell order
      if (window.App.type === constants.APP_TYPE_DEFAULT && this.currentChoiceConfig.type !== constants.ORDER_STEP_UP_SELL) {
        this.deleteAlreadyOrderedOnOrionRedis(this.getStoragePreviousChoice().productId);
      }

      let capturedNow = false;

      if (this.$cookies.get('CAPTURED_NOW')) {
        capturedNow = true;
      }

      const orionApi = new OrionApi(this.$store, capturedNow);
      orionApi.patchOrder(orderData).then(response => {
        this.$log.debug('Order call to Orion successful', response);
        this.$store.commit(mutations.SET_IS_PREVIOUS_ORDER_CANCELLED, true);

        const pathElements = window.location.pathname.split('/');

        // Apply the desired behaviour after the click
        switch (cancelConfig.type) {
          case 'static':
            window.location = this.appendQueryParamsBeforeRedirection(`${window.location.origin}/static/${this.locale}/cancel.html`);
            break;
          case 'step':
            // Redirect to the specific step
            if (process.env.IS_LOCAL === '1') {
              pathElements[3] = cancelConfig.step;
            } else {
              pathElements[2] = cancelConfig.step;
            }

            // Set the destination page to cancel.html and no index.html
            pathElements[pathElements.length - 1] = 'cancel.html';

            window.location = this.appendQueryParamsBeforeRedirection(window.location.origin + pathElements.join('/'));
            break;
          case 'url':
            window.location = this.appendQueryParamsBeforeRedirection(cancelConfig.url);
            break;
          default:
            break;
        }
      }).catch(error => {
        this.sendSentryLog(
          Severity.Fatal,
          'Update order call to Orion failed',
          {
            error,
            action: 'cancel order'
          });
      });
    }
  }
};



// WEBPACK FOOTER //
// ./src/common/mixins/order.js